import type {
  ApprovalAudienceDocument,
  ApprovalConditionField,
  ApprovalPolicyCondition,
  ApprovalPolicyDocument,
  ApprovalRecordRole,
  ApprovalRequirement,
  ApprovalSubject,
} from "@common/client"
import {
  ClipboardCheck,
  ContactRound,
  FileCheck,
  type LucideIcon,
  MessageSquareWarning,
  ReceiptText,
  TriangleAlert,
  UserRoundCheck,
} from "lucide-react"
import {
  type AudienceItem,
  type ConditionPhrase,
  type Names,
  named,
  namedPhrase,
  phrase,
  type RecordKind,
  spoken,
  staffAudienceItem,
  texts,
} from "@/routes/_layout/settings/_panel/-lib/flow"

export const SUBJECTS = [
  "squawk_review",
  "squawk_verify",
  "work_task",
  "work_task_step",
  "canned_work_task_version",
  "quote_task_review",
  "quote_task_customer",
] as const satisfies readonly ApprovalSubject[]

export const SUBJECT_LABELS: Record<
  ApprovalSubject,
  { label: string; trigger: string; icon: LucideIcon }
> = {
  squawk_review: {
    label: "Squawk review",
    trigger: "A squawk needs review",
    icon: TriangleAlert,
  },
  squawk_verify: {
    label: "Squawk verify",
    trigger: "A reviewed squawk needs verifying",
    icon: MessageSquareWarning,
  },
  work_task: {
    label: "Task sign-off",
    trigger: "A task needs signing off",
    icon: ClipboardCheck,
  },
  work_task_step: {
    label: "Step sign-off",
    trigger: "A task step needs signing off",
    icon: ClipboardCheck,
  },
  canned_work_task_version: {
    label: "Template tasks",
    trigger: "A template task version needs review",
    icon: FileCheck,
  },
  quote_task_review: {
    label: "Quote review",
    trigger: "Quote tasks need review",
    icon: ReceiptText,
  },
  quote_task_customer: {
    label: "Customer approval",
    trigger: "Quote tasks go to the customer",
    icon: ReceiptText,
  },
}

export const REQUIREMENTS: Record<ApprovalRequirement, string> = {
  technician: "technician sign-off",
  inspector: "inspection",
  reviewer: "review",
  verifier: "verification",
  customer: "customer approval",
}

const RECORD_ROLES: Record<
  ApprovalRecordRole,
  { label: string; detail: string }
> = {
  assigned_reviewers: {
    label: "Assigned reviewers",
    detail: "People assigned to review it",
  },
  lead_technician: {
    label: "Lead technician",
    detail: "The work order's lead technician",
  },
  inspector: { label: "Inspector", detail: "The work order's inspector" },
  customer_contacts: {
    label: "Customer contacts",
    detail: "Contacts at the quote's customer",
  },
  approval_contact: {
    label: "Approval contact",
    detail: "The contact the quote names for approvals",
  },
}

type Field =
  | {
      kind: "record" | "team"
      label: string
      removed: string
      record: RecordKind
    }
  | { kind: "choice"; label: string }

export const FIELDS: Record<ApprovalConditionField, Field> = {
  work_package: {
    kind: "record",
    label: "Work order",
    removed: "a removed work order",
    record: "work_order",
  },
  customer: {
    kind: "record",
    label: "Customer",
    removed: "a removed customer",
    record: "customer",
  },
  aircraft: {
    kind: "record",
    label: "Aircraft",
    removed: "a removed aircraft",
    record: "aircraft",
  },
  department: {
    kind: "record",
    label: "Department",
    removed: "an archived department",
    record: "department",
  },
  facility: {
    kind: "record",
    label: "Facility",
    removed: "an archived facility",
    record: "facility",
  },
  severity: { kind: "choice", label: "Severity" },
  requester_team: {
    kind: "team",
    label: "Requester's team",
    removed: "an archived team",
    record: "team",
  },
}

export function approverItem(
  audience: ApprovalAudienceDocument,
  names: Names,
): AudienceItem {
  switch (audience.kind) {
    case "record_role":
      return {
        icon: UserRoundCheck,
        ...RECORD_ROLES[audience.record_role],
        missing: false,
      }
    case "customer_contact":
      return named(
        ContactRound,
        names[audience.customer_contact_id],
        "Customer contact",
        "Removed contact",
      )
    default:
      return staffAudienceItem(audience, names, "whoever asked for approval")
  }
}

function teamPhrase(
  condition: ApprovalPolicyCondition,
  names: Names,
  removed: string,
): ConditionPhrase {
  switch (condition.op) {
    case "is_set":
      return phrase("Requested by someone in", [
        condition.value === true ? "a team" : "no team",
      ])
    case "is_not":
      return namedPhrase(
        "Requested by someone outside",
        texts(condition.value),
        names,
        removed,
      )
    case "is":
    case "is_any_of":
      return namedPhrase(
        "Requested by someone in",
        texts(condition.value),
        names,
        removed,
      )
  }
}

function lead(label: string, condition: ApprovalPolicyCondition): string {
  return condition.op === "is_not" ? `${label} is not` : `${label} is`
}

function conditionPhrase(
  condition: ApprovalPolicyCondition,
  names: Names,
): ConditionPhrase {
  const field = FIELDS[condition.field]
  switch (field.kind) {
    case "team":
      return teamPhrase(condition, names, field.removed)
    case "record":
      return condition.op === "is_set"
        ? phrase(`${field.label} is`, [condition.value ? "set" : "not set"])
        : namedPhrase(
            lead(field.label, condition),
            texts(condition.value),
            names,
            field.removed,
          )
    case "choice":
      return phrase(
        lead(field.label, condition),
        texts(condition.value).map(spoken),
      )
  }
}

export function policyPhrases(
  policy: ApprovalPolicyDocument,
  names: Names,
): ConditionPhrase[] {
  return (policy.conditions ?? []).map((condition) =>
    conditionPhrase(condition, names),
  )
}

export function byCheckOrder(
  first: ApprovalPolicyDocument,
  second: ApprovalPolicyDocument,
): number {
  return (
    Number(first.is_default ?? false) - Number(second.is_default ?? false) ||
    (first.priority ?? 0) - (second.priority ?? 0) ||
    first.name.localeCompare(second.name)
  )
}
